import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { BehaviorSubject, ReplaySubject, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { Timeout } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { closeRtcPeer } from 'app/utils/media/rtc';
import { cleanTracks } from 'app/utils/media/stream';
import User from 'entity/User';
import {
  ScreenShareStatus,
  IncomingMessage,
  IncomingMessageType,
  SharingUnsubscribeMessage,
  OutgoingMessageType,
  SharingSubscribeCandidateMessage,
  SharingSubscribeMessage,
  SharingSubscribeAnswerMessage,
  PeerJsep,
  EndSharingMessage,
  PeerCandidate,
  MirrorType,
  UpdateType,
} from 'messages/websocket.messages';

import { StageSizeType } from '../../performer-participant/performer-participant.types';
import ParticipantRtcStats from '../../preciate-stream/participant-rtc-stats';
import { MediaStreamWrapper } from '../../preciate-stream/room-participant';
import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import { SubscriptionState } from '../../types/webrtc';
import { MirrorMediaStreams, PresentationService } from '../presentation/presentation.service';
import { createMirrorId } from '../presentation/util';
import { RtcStatsStreamService, StatsCounter } from '../rtc-stats-stream';
import { SubscriptionReason, SubscriptionType } from '../subscription/subscription.types';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

const EMPTY_STREAM = { id: uuidv4(), stream: undefined };
const SCREEN_SHARE_SUBSCRIPTION_ID = 'primary-screen-share';

@Injectable({
  providedIn: 'root',
})
export class ShareScreenParticipantService extends Loggable {
  private destroyed$ = new ReplaySubject<boolean>(1); // used to manage unloading all subscriptions
  private isHost = false;
  private mediaStreamSource = new BehaviorSubject<MediaStreamWrapper>(EMPTY_STREAM);
  private mirrorId?: string;
  private mirrorSubscription?: Subscription;
  private _peer?: RTCPeerConnection;
  private peerConnectionTime?: number;
  private retryTimeout?: Timeout;
  private remoteShareSocialId?: string;
  private screenSharingUserId?: string;
  private sessionId?: string;
  private socialId = '';
  private stream?: MediaStream;
  private user?: User;
  private isCrossRoomPresentation = false;
  private _subscriptionState: SubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
  private rtcStats?: ParticipantRtcStats;
  private rtcVideoStatsInterval?: Timeout;

  public mediaStream$ = this.mediaStreamSource.asObservable();
  public viewSize: StageSizeType = StageSizeType.MEDIUM;
  private shareSubscriptionState: SubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
  private createPeerTimeout: number | undefined;
  // janus 1.2 requires we wait for this message before creating the peer
  private screenshareStartMessage = false;

  public constructor(
    private websocketService: WebsocketService,
    private videoRoomService: VideoRoomService,
    private presentationService: PresentationService,
    private rtcStatsStreamService: RtcStatsStreamService,
    private store: Store,
  ) {
    super();
    this.setDisplayName('ShareScreenParticipantService');
    this.websocketService.message$.subscribe(this.wsMessageListener);

    // User state
    this.store.select(coreSelectors.getUserEntity).subscribe((user) => (this.user = user));

    // Social state
    this.store.select(socialAppSelectors.getSocialSessionId).subscribe((sessionId) => (this.sessionId = sessionId));
    this.store.select(socialAppSelectors.getJoinedSocialId).subscribe((socialId) => (this.socialId = socialId ?? ''));
    this.store.select(socialAppSelectors.isHost).subscribe((isHost) => (this.isHost = isHost));

    // Presentation state
    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .subscribe((isCrossRoomPresentation) => (this.isCrossRoomPresentation = isCrossRoomPresentation));

    // Screen share state
    combineLatest([this.store.select(socialAppSelectors.getRemoteScreenSharePresenterV1)])
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(([presenterV1]) => {
        const presenter = presenterV1;
        if (this.screenSharingUserId === presenter?.userId) {
          return;
        }
        this.mirrorId = presenter ? createMirrorId(presenter, MirrorType.SCREEN_SHARE) : undefined;
        this.screenSharingUserId = presenter?.userId;
        this.mirrorSubscription?.unsubscribe();
        this.remoteShareSocialId = presenter?.socialId;
        this.handleCrossMirrors();
      });

    this.store
      .select(socialAppSelectors.getScreenShareState)
      .pipe(pairwise())
      .subscribe(([oldScreenShareState, newScreenShareState]) => {
        const { status: oldStatus } = oldScreenShareState;
        const { status: newStatus } = newScreenShareState;
        const { userId: oldPresenter } = oldScreenShareState.screenShare?.presenter ?? {};
        const { socialId, userId: newPresenter } = newScreenShareState.screenShare?.presenter ?? {};
        // Clear peer if screen share is no longer active, or if the presenter has changed
        if (
          (newStatus === ScreenShareStatus.NONE && oldStatus === ScreenShareStatus.ACTIVE) ||
          newPresenter !== oldPresenter
        ) {
          if (document.pictureInPictureEnabled) {
            void document.exitPictureInPicture();
          }
          void this.clear();
        }

        this.screenSharingUserId = newPresenter;
        // Create peer for active screen share
        if (
          (newStatus === ScreenShareStatus.ACTIVE && oldStatus === ScreenShareStatus.NONE) ||
          newPresenter !== oldPresenter
        ) {
          if (!newPresenter) {
            this.warn('Presenter user id is undefined at screen share start, cannot show screen share');
            return;
          }
          // If the social ID matches, this is a local screen share peer. Subscribe to it directly.
          // Cross-room screen shares are handled as mirror peers.
          if (socialId === this.socialId) {
            this.info('Starting screen share from user', { newPresenter });
            this.createPeerTimeout = Date.now() + 5000;
            this.createPeerWhenReady();
          }
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.screenshare_start_message)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((screenshare_start_message) => {
        this.screenshareStartMessage = screenshare_start_message;
      });
  }

  private createPeerWhenReady(): void {
    if (
      !this.screenshareStartMessage ||
      this.shareSubscriptionState === SubscriptionState.SUBSCRIBED ||
      (this.createPeerTimeout && this.createPeerTimeout < Date.now())
    ) {
      this.createPeer();
    } else {
      setTimeout(() => {
        this.createPeerWhenReady();
      }, 1000);
    }
  }

  /**
   * End the current screen share
   *
   * @returns boolean indicating if the screen share was able to be ended
   */
  public async endScreenSharing(): Promise<boolean> {
    if (!this.isHost) {
      this.error('endScreenSharing: Could not end sharing, user is not a host');
      return false;
    }

    if (!this.sessionId) {
      this.error('endScreenSharing: Could not end sharing, sessionId is undefined');
      return false;
    }

    if (!this.screenSharingUserId) {
      this.error('endScreenSharing: Could not end sharing, screenSharingUserId is undefined');
      return false;
    }

    assertDefined(this.user?.userId);
    const { userId } = this.user;

    await SocialStateMachine.shared
      .requestEndScreenShare({
        presenterId: this.screenSharingUserId,
        userId,
        updateType: this.isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .then(() => {
        this.info('Successfully ended screen share for participant');
        this.shareSubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
      })
      .catch((e) => this.error('Failed to end screen share for participant', e));

    const msg: EndSharingMessage = {
      t: OutgoingMessageType.SHREND,
      socialId: this.remoteShareSocialId ?? this.socialId,
    };
    this.websocketService.sendMessage(msg);

    // Clear peer connection
    await this.clear();
    return true;
  }

  // -- CREATE and CLEAR Peer --------------------------------------------------------------------------------------

  public async clear(): Promise<void> {
    this.store.dispatch(socialAppActions.REMOVE_SUBSCRIPTION({ subscriptionId: SCREEN_SHARE_SUBSCRIPTION_ID }));
    this.shareSubscriptionState = SubscriptionState.NOT_SUBSCRIBED;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    await cleanTracks(this.stream, this);
    this.stream = undefined;
    this.retryTimeout = undefined;

    if (!this.peer) {
      return;
    }
    this.info('[4] Close ', this.screenSharingUserId);
    if (this.screenSharingUserId) {
      try {
        const msg: SharingUnsubscribeMessage = {
          t: OutgoingMessageType.SHRUNSUB,
          sharingUserId: this.screenSharingUserId,
          groupShare: false,
        };
        this.websocketService.sendMessage(msg);
      } catch (e) {
        this.error('Error Unsubscribing to screen share: ', e);
      }
    }

    // Remove RTC Stats Interval
    this.destroyRtcStats();

    closeRtcPeer(this.peer);
    this.peer = undefined;
  }

  public async destroy(): Promise<void> {
    await this.clear();

    // Remove RTC Stats Interval
    this.destroyRtcStats();

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // Websocket Message Listener
  private wsMessageListener = async (msg: IncomingMessage): Promise<void> => {
    if (msg.t === IncomingMessageType.SHR_SUBOFF) {
      const { d, groupShare } = msg;
      if (!groupShare) {
        this.info('ShareScreenParticipant message received', msg);
        await this.setDescription(d as RTCSessionDescriptionInit);
      }
    } else if (msg.t === IncomingMessageType.SHR_STARTED) {
      this.info('ShareScreenParticipant message received', msg);
      this.shareSubscriptionState = SubscriptionState.SUBSCRIBED;
    } else if (msg.t === IncomingMessageType.SHR_ENDED) {
      this.info('ShareScreenParticipant message received', msg);
      this.shareSubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
    }
  };

  private handleCrossMirrors(): void {
    this.mirrorSubscription = this.presentationService.mirrorMediaStreams$
      .pipe(
        map((streams: MirrorMediaStreams) => {
          const { video: stream, videoId: id } = streams[this.mirrorId ?? ''] ?? {};
          return { id, stream };
        }),
        filter(({ id }) => !!id),
      )
      .subscribe(({ id, stream }) => this.handleMediaStreamChange(stream, id));
  }

  private handleMediaStreamChange(stream: MediaStream | undefined, id: string) {
    if (!stream) {
      return;
    }
    this.info('Received new mirror screen share stream', { id });
    this.mediaStreamSource.next({ id, stream });
  }

  private createPeer(): void {
    if (!this.sessionId) {
      this.error('Not Valid:: sessionId', { sessionId: this.sessionId });
      return;
    }

    // Create Peer Connection
    this.info('[1] Creating Share scree participant Peer ', this.screenSharingUserId);

    // Setup WebRTC Peer and Tracks
    const rtcConfig = this.videoRoomService.createRtcConfig();
    const peer = new RTCPeerConnection(rtcConfig);
    const peerTime = Date.now();

    // Connection State Change
    peer.onconnectionstatechange = () => {
      // Peer may have already been destroyed. There's nothing to do in this case.
      if (!this.peer) {
        return;
      }
      const { connectionState } = this.peer;
      this.info('Share screen Participant Subscribe state change', { connectionState });
      switch (connectionState) {
        case 'connected':
          this.subscriptionState = SubscriptionState.SUBSCRIBED;
          this.peerConnectionTime = Date.now();
          this.startRtcStats();
          break;

        case 'failed':
          this.error('Share screen participant Peer connection failed', { connectionState });
          this.subscriptionState = SubscriptionState.FAILED;

          this.handleConnectionFailed();
          break;

        default:
          break;
      }
    };

    // On ice connection state change
    peer.oniceconnectionstatechange = (): void => {
      const { iceConnectionState } = this.peer || {};

      this.info('Subscribe ICE state change', { iceConnectionState, peerTime });
    };

    // On ice gathering state change
    peer.onicegatheringstatechange = (): void => {
      const { iceGatheringState } = this.peer || {};
      this.info('Subscribe ICE gathering state change', { iceGatheringState, peerTime });
    };

    // On ice candidate error
    peer.onicecandidateerror = (e) => this.info('Candidate error', this.screenSharingUserId, e);

    this.peer = peer;

    this.peer.ontrack = (event) => this.onTrack(event);
    this.peer.onicecandidate = (event) => this.sendCandidate(event);

    if (!this.screenSharingUserId) {
      this.error('Screen sharing user id is undefined, cannot subscribe to screen share');
      this.retrySubscription();
      return;
    }
    this.info('Screen Share participant subscribe', { peerTime });

    const msg: SharingSubscribeMessage = {
      t: OutgoingMessageType.SHRSUB,
      sharingUserId: this.screenSharingUserId,
      groupShare: false,
    };
    this.websocketService.sendMessage(msg);
  }

  private async setDescription(offer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peer) {
      this.error('Could not set description for RTCPeer connection, peer is not set');
      return;
    }

    this.info('[2] Setting Description ', this.screenSharingUserId);
    await this.peer.setRemoteDescription(offer);
    this.peer.getTransceivers().forEach((t) => (t.direction = 'recvonly'));
    const answer = await this.peer.createAnswer();
    await this.peer.setLocalDescription(answer);

    if (!answer.type) {
      this.error('setDescription: answer type is unexpectedly undefined', answer);
      return;
    }

    if (!this.screenSharingUserId) {
      this.error('Could not send SharingSubscribeAnswerMessage, screen sharing user id is not set');
      return;
    }
    const msg: SharingSubscribeAnswerMessage = {
      t: OutgoingMessageType.SHRSUBANS,
      sharingUserId: this.screenSharingUserId,
      groupShare: false,
      answer: answer as PeerJsep,
    };
    this.websocketService.sendMessage(msg);
  }

  private sendCandidate(event: RTCPeerConnectionIceEvent): void {
    const { candidate } = event;
    if (!candidate) {
      this.info('ICE Candidate was null, done');
      return;
    }

    this.info('ICE Candidate received', candidate.toJSON());
    const msg: SharingSubscribeCandidateMessage = { t: OutgoingMessageType.SHRSUBCAN, d: candidate as PeerCandidate };
    this.websocketService.sendMessage(msg);
  }

  private onTrack(event: RTCTrackEvent): void {
    this.info('[3] Screen share Track Video received', this.screenSharingUserId);
    const streams = event.streams;
    for (const stream of streams) {
      this.mediaStreamSource.next({ id: uuidv4(), stream });
    }
  }

  private handleConnectionFailed(): void {
    this.rtcStatsStreamService.incrementCounter(StatsCounter.SCREENSHARE_SUBSCRIBER_CONNECTION_FAILED);
    const oneMinuteAgo = moment().subtract(1, 'minute');
    const connectionTime = this.peerConnectionTime;

    // If the connection fails in under a minute, or we never connected, advance the reconnection state machine to
    // potentially more reliable methods
    if (!connectionTime || connectionTime > oneMinuteAgo.valueOf()) {
      this.videoRoomService.advanceReconnectionState({ connectionTime });
      // Clear the current connection time to avoid advancing state twice
      this.peerConnectionTime = undefined;
    }

    this.retrySubscription();
  }

  private retrySubscription(): void {
    this.subscriptionState = SubscriptionState.RESUBSCRIBE;
    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = undefined;
      this.createPeer();
    }, 1500);
  }

  // -- RTC STats --------------------------------------------------------------------------------------
  private startRtcStats(): void {
    this.info('RTC Stats START for share screen participant');
    const rtcStats = (this.rtcStats = new ParticipantRtcStats(''));
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
    }
    this.rtcVideoStatsInterval = setInterval(async () => {
      // Validate if peer and videoTrack is still available
      let isVideoStuck = false;

      if (!this.peer) {
        return;
      }

      const stats = await this.peer.getStats();
      isVideoStuck = rtcStats.isVideoStuck(stats, 'screenshare', 'participant');

      if (isVideoStuck) {
        this.warn('Screen share participant Media is Stuck RESUBSCRIBE', {
          isVideoStuck,
        });
        this.destroyRtcStats();
        this.handleConnectionFailed();
      }
    }, 1000);
  }

  private destroyRtcStats(): void {
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
    }
    if (this.rtcStats) {
      this.info('Group share RTC Stats STOP');
    }
    this.rtcStats?.destroy();
    this.rtcVideoStatsInterval = undefined;
    this.rtcStats = undefined;
  }

  private set subscriptionState(state: SubscriptionState) {
    this.info(`Subscription state ${this._subscriptionState} -> ${state}`);
    this._subscriptionState = state;
    this.store.dispatch(
      socialAppActions.CHANGE_SUBSCRIPTION_STATUS({
        status: {
          name: SCREEN_SHARE_SUBSCRIPTION_ID,
          id: SCREEN_SHARE_SUBSCRIPTION_ID,
          state,
          videoNeeded: true,
          audioNeeded: true,
          reason: SubscriptionReason.SCREEN_SHARE,
          type: SubscriptionType.SCREEN_SHARE,
          peerTime: 0,
        },
      }),
    );
  }

  public get peer(): RTCPeerConnection | undefined {
    return this._peer;
  }

  private set peer(newPeer: RTCPeerConnection | undefined) {
    if (newPeer) {
      this.rtcStatsStreamService.addSubscriptionMonitoring(
        SCREEN_SHARE_SUBSCRIPTION_ID,
        SubscriptionType.SCREEN_SHARE,
        newPeer,
      );
    } else {
      this.rtcStatsStreamService.removeSubscriptionMonitoring(SCREEN_SHARE_SUBSCRIPTION_ID);
    }
    this._peer = newPeer;
  }

  // #region Test Utilities

  /**
   * Simulate peer close
   * @param userId Close the peer for this userId
   */
  public closeSubscriberPeer(): void {
    if (!this.peer) {
      this.warn('Subscriber peer not found');
      return;
    }

    this.subscriptionState = SubscriptionState.FAILED;
    closeRtcPeer(this.peer);
  }

  // #endregion
}
